import { Button, Modal, Radio } from "antd";
import { CheckboxGroupProps } from "antd/es/checkbox";
import { CustomTable, useCustomToast } from "../../../../components";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { OnlineInventoryAPI } from "../../api/onlineInventoryAPI";
import { UserInventory } from "../../types/entry-status";
import { useAtom } from "jotai";
import { inventoryIdAtom, inventoryStatusAtom } from "../../../../state/atoms";
import apiClient from "../../../../services/apiClient";

interface OnlineInventoryVerifyProps {
  visible: boolean;
  onClose: () => void;
}

const OnlineInventoryVerify: React.FC<OnlineInventoryVerifyProps> = ({
  visible,
  onClose,
}) => {
  const navigate = useNavigate();
  const [isNewsOp, setIsNewsOp] = useState(false);
  const [tableData, setTableData] = useState<UserInventory[]>([]);
  const [, setInventoryStatus] = useAtom(inventoryStatusAtom);
  const [, setInventoryId] = useAtom(inventoryIdAtom);
  const [selectedInventoryId, setSelectedInventoryId] = useState<number | null>(null);
  const { showToast } = useCustomToast();

  const downloadExcel = async (url: string, fallbackName = 'archivo_descargado') => {
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: '*/*',
        },
      });

      if (!response.ok) {
        let errorMsg = 'Ocurrió un error al descargar el archivo.';
        try {
          const errorJson = await response.clone().json();
          errorMsg = errorJson?.message || errorMsg;
        } catch (e) {
          throw e;
        }
        showToast('error', errorMsg);
        return;
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      const match = contentDisposition?.match(/filename="?([^"]+)"?/);
      const filename = match?.[1] || fallbackName;

      const urlBlob = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlBlob;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(urlBlob);
    } catch (error: any) {
      showToast('error', error?.response?.data?.message || 'Ocurrió un error al descargar el archivo.');
    }
  };


   const downloadPdf = async (url: string, fallbackName = 'archivo_descargado') => {
    const token = localStorage.getItem('token');
    try {
      const { data } = await apiClient.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: '*/*',
        },
      });

      const fileResponse = await fetch(data.url);
      const blob = await fileResponse.blob();

      const fileUrl = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = fileUrl;
      downloadLink.setAttribute('download', data.filename || fallbackName);
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      URL.revokeObjectURL(fileUrl);
    } catch (error: any) {
      showToast('error', error?.response?.data?.message || 'Ocurrió un error al descargar el archivo.');
    }
  };

  const options: CheckboxGroupProps<string>['options'] = [
    { label: 'Inventarios de verificación', value: 'verification' },
    { label: 'Novedades', value: 'news' },
  ];

  const columnsNews = [
    {
      title: 'Fechas',
      key: 'date',
      dataIndex: 'date'
    },
    {
      title: 'Acciones',
      key: 'route_report',
      dataIndex: 'route_report',
      render: (route_report: string) => (
        <Button
          type="link"
          onClick={() => downloadExcel(route_report)}
        >
          Reporte de novedades
        </Button>
      ),
    },
  ];

  const columnsVerification = [
    {
      title: 'Inventario',
      dataIndex: 'inventory_name',
      key: 'inventory_name',
    },
    {
      title: 'Fecha inicial',
      dataIndex: 'start_date',
      key: 'start_date',
    },
    {
      title: 'Fecha final',
      dataIndex: 'end_date',
      key: 'end_date',
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => status || 'N/A',
    },
    {
      title: 'Acciones',
      dataIndex: 'actions',
      key: 'actions',
      render: (_: any, record: any) => {
        if (record.status_id === '1' && record.inventory_status_id !== 7) {
          return (
            <Button
              type="link"
              onClick={() => {
                setInventoryId(record.id)
                setSelectedInventoryId(record.id);
                handleVerificationClick(record.id, record.status)
              }}
            >
              Verificación física del bien
            </Button>
          );
        } else {
            return (
              <>
                <Button
                  type="link"
                  onClick={() => {
                    downloadPdf(record.route_verification_report)
                  }}
                >
                  Reporte de verificación
                </Button>
                <Button
                  type="link"
                  onClick={() => {
                    downloadExcel(record.route_event_report)
                  }}
                >
                  Reporte de novedades
                </Button>
              </>
            );
        }
      },
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await OnlineInventoryAPI.getUserInventories();
        const rawData: UserInventory[] = response.data;

        setTableData(rawData);
      } catch (error) {
        console.error("Error al obtener inventarios", error);
      }
    };

    fetchData();
  }, []);

 const filteredData = isNewsOp
  ? tableData.flatMap(it => it.event_dates || [])
  : tableData.filter(it => it.id);

  const handleVerificationClick = async (inventoryId: number, status: string) => {
    try {
      if (status === 'Por verificar') {
        await OnlineInventoryAPI.changeInventoryStatus(inventoryId, 26);
        setInventoryStatus('En proceso');
      }
      onClose();
      navigate('/onlineinventory-programmed');
    } catch (error) {
      showToast('error', 'Error al actualizar el estado del inventario')
    }
  };

  return (
    <Modal
      title={"Procesos archivados"}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
    >
      <section className="mt-8">
        <Radio.Group
          options={options}
          defaultValue="verification"
          onChange={(e) => {
            setIsNewsOp(e.target.value === 'news');
          }}
        />
      </section>

      <section className="mt-8">
        <CustomTable
          columns={isNewsOp ? columnsNews : columnsVerification}
          dataSource={filteredData as any[]}
          loading={false}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
          size="small"
        />
      </section>

      <section style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          onClick={() => {
            onClose();
          }}
          className="mt-16 pl-32 pr-32"
        >
          Finalizar
        </Button>
      </section>
    </Modal>
  );
}

export default OnlineInventoryVerify;